import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DollarSign,
  TrendingUp,
  Download,
  Filter,
  RefreshCw,
} from 'lucide-react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { api } from '../../api/client';

type PaymentStatus = 'all' | 'paid' | 'pending' | 'failed' | 'refunded';
type Provider = 'all' | 'cryptocloud' | 'cloudpayments' | 'dlocal' | 'paddle';

interface Payment {
  id: string;
  date: string;
  user_email: string;
  amount: number;
  currency: string;
  provider: Provider;
  plan: string;
  status: Exclude<PaymentStatus, 'all'>;
  invoice_url?: string;
}

interface PaymentsResponse {
  items: Payment[];
  total_mrr: number;
  total_revenue: number;
}

const PROVIDERS: Provider[] = ['all', 'cryptocloud', 'cloudpayments', 'dlocal', 'paddle'];
const STATUSES: PaymentStatus[] = ['all', 'paid', 'pending', 'failed', 'refunded'];

const STATUS_BADGE: Record<Exclude<PaymentStatus, 'all'>, string> = {
  paid: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-yellow-100 text-yellow-700',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-slate-100 text-slate-600',
};

const PROVIDER_LABEL: Record<Provider, string> = {
  all: 'All Providers',
  cryptocloud: 'CryptoCloud',
  cloudpayments: 'CloudPayments',
  dlocal: 'dLocal',
  paddle: 'Paddle',
};

async function fetchPayments(params: {
  dateFrom?: string;
  dateTo?: string;
  provider: Provider;
  status: PaymentStatus;
}): Promise<PaymentsResponse> {
  const { data } = await api.get<PaymentsResponse>('/admin/payments', {
    params: {
      date_from: params.dateFrom || undefined,
      date_to: params.dateTo || undefined,
      provider: params.provider !== 'all' ? params.provider : undefined,
      status: params.status !== 'all' ? params.status : undefined,
    },
  });
  return data;
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: typeof DollarSign;
  label: string;
  value: string;
  color: string;
}): JSX.Element {
  return (
    <div className="card flex items-center gap-4">
      <div className={clsx('p-3 rounded-xl', color)}>
        <Icon size={22} />
      </div>
      <div>
        <div className="text-2xl font-bold text-brand-ink">{value}</div>
        <div className="text-sm text-brand-muted">{label}</div>
      </div>
    </div>
  );
}

export function AdminPayments(): JSX.Element {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [provider, setProvider] = useState<Provider>('all');
  const [status, setStatus] = useState<PaymentStatus>('all');

  const paymentsQ = useQuery({
    queryKey: ['admin', 'payments', dateFrom, dateTo, provider, status],
    queryFn: () => fetchPayments({ dateFrom, dateTo, provider, status }),
    placeholderData: (prev) => prev,
  });

  const data = paymentsQ.data;
  const payments = data?.items ?? [];

  const resetFilters = (): void => {
    setDateFrom('');
    setDateTo('');
    setProvider('all');
    setStatus('all');
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-brand-ink">Payments</h1>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {paymentsQ.isLoading ? (
          <>
            <div className="card h-20 animate-pulse bg-slate-100" />
            <div className="card h-20 animate-pulse bg-slate-100" />
          </>
        ) : (
          <>
            <SummaryCard
              icon={TrendingUp}
              label="Monthly Recurring Revenue"
              value={`$${(data?.total_mrr ?? 0).toLocaleString()}`}
              color="bg-brand-primary/10 text-brand-primary"
            />
            <SummaryCard
              icon={DollarSign}
              label="Total Revenue (filtered)"
              value={`$${(data?.total_revenue ?? 0).toLocaleString()}`}
              color="bg-emerald-50 text-emerald-600"
            />
          </>
        )}
      </div>

      {/* Filters */}
      <div className="card space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-brand-ink">
          <Filter size={15} /> Filters
          <button
            onClick={resetFilters}
            className="ml-auto text-xs text-brand-muted hover:text-brand-ink flex items-center gap-1"
          >
            <RefreshCw size={12} /> Reset
          </button>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-brand-muted">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="input text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-brand-muted">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="input text-sm"
            />
          </div>

          <div className="flex gap-1 flex-wrap">
            {PROVIDERS.map((p) => (
              <button
                key={p}
                onClick={() => setProvider(p)}
                className={clsx(
                  'px-2.5 py-1 rounded-lg text-xs font-medium border transition',
                  provider === p
                    ? 'bg-brand-primary text-white border-brand-primary'
                    : 'border-slate-200 text-brand-ink hover:border-slate-300',
                )}
              >
                {PROVIDER_LABEL[p]}
              </button>
            ))}
          </div>

          <div className="flex gap-1 flex-wrap">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={clsx(
                  'px-2.5 py-1 rounded-lg text-xs font-medium border transition capitalize',
                  status === s
                    ? 'bg-brand-primary text-white border-brand-primary'
                    : 'border-slate-200 text-brand-ink hover:border-slate-300',
                )}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        {paymentsQ.isLoading ? (
          <div className="space-y-2 p-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />
            ))}
          </div>
        ) : payments.length === 0 ? (
          <p className="text-brand-muted text-sm p-4">No payments found.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-brand-muted border-b border-slate-100">
                <th className="pb-3 font-medium">Date</th>
                <th className="pb-3 font-medium">User</th>
                <th className="pb-3 font-medium">Amount</th>
                <th className="pb-3 font-medium">Provider</th>
                <th className="pb-3 font-medium">Plan</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors">
                  <td className="py-3 text-brand-muted whitespace-nowrap">
                    {dayjs(p.date).format('DD MMM YYYY')}
                  </td>
                  <td className="py-3 text-brand-ink font-medium max-w-[180px] truncate">
                    {p.user_email}
                  </td>
                  <td className="py-3 font-semibold text-brand-ink whitespace-nowrap">
                    {p.amount} {p.currency}
                  </td>
                  <td className="py-3">
                    <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-700 font-medium">
                      {PROVIDER_LABEL[p.provider] ?? p.provider}
                    </span>
                  </td>
                  <td className="py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-brand-primary/10 text-brand-primary font-medium">
                      {p.plan}
                    </span>
                  </td>
                  <td className="py-3">
                    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-semibold', STATUS_BADGE[p.status])}>
                      {p.status}
                    </span>
                  </td>
                  <td className="py-3">
                    {p.invoice_url ? (
                      <a
                        href={p.invoice_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-brand-primary hover:underline text-xs"
                      >
                        <Download size={13} /> PDF
                      </a>
                    ) : (
                      <span className="text-brand-muted text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
